import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowRight, Building2, Compass, UsersRound } from "lucide-react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { getPublicContentFn } from "@/lib/app.functions"

export const Route = createFileRoute("/about/")({
  loader: async () => {
    const [company, team, partners, testimonials, faq] = await Promise.all([
      getPublicContentFn({ data: { kind: "page", slug: "company" } }),
      getPublicContentFn({ data: { kind: "team" } }),
      getPublicContentFn({ data: { kind: "partner" } }),
      getPublicContentFn({ data: { kind: "testimonial" } }),
      getPublicContentFn({ data: { kind: "faq" } }),
    ])
    return { company: company.at(0), team, partners, testimonials, faq }
  },
  head: () => ({
    meta: [{ title: "About this demo · Harbor & Pine Realty" }],
  }),
  component: AboutPage,
})

function AboutPage() {
  const { company, team, partners, testimonials, faq } = Route.useLoaderData()

  return (
    <>
      <section className="border-b-2 border-foreground">
        <div className="field-container grid gap-8 py-14 lg:grid-cols-[1.1fr_0.9fr] lg:py-20">
          <div>
            <p className="field-label">About the demonstration</p>
            <h1 className="field-heading mt-3">
              An invented agency. A complete product.
            </h1>
          </div>
          <div className="self-end">
            <p className="text-lg leading-8 text-muted-foreground">
              {company?.body ??
                "Harbor & Pine is a fictional identity created to demonstrate a complete real-estate portal."}
            </p>
            <Link
              to="/contact"
              search={{ subject: undefined }}
              className="field-link mt-6 inline-flex items-center gap-2"
            >
              Contact the demo desk <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="field-container py-16">
        <div className="grid gap-px border bg-border md:grid-cols-3">
          {[
            {
              icon: Compass,
              title: "Public discovery",
              copy: "Search, exact property maps, brochures, galleries, content, events, sharing, and inquiry.",
            },
            {
              icon: UsersRound,
              title: "Member workspace",
              copy: "Favorites, saved searches, alerts, profile, password, inquiries, and owner listings.",
            },
            {
              icon: Building2,
              title: "Operations desk",
              copy: "Properties, users, roles, inbox, public content, taxonomies, regions, currencies, and subscribers.",
            },
          ].map((item) => (
            <article
              key={item.title}
              className="field-rule min-h-64 bg-white p-6"
            >
              <item.icon className="size-7 text-primary" />
              <h2 className="mt-16 font-heading text-3xl uppercase">
                {item.title}
              </h2>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {item.copy}
              </p>
            </article>
          ))}
        </div>
      </section>

      {team.length > 0 ? (
        <section className="border-y-2 border-foreground bg-foreground text-white">
          <div className="field-container py-16">
            <p className="field-label text-white/55">Fictional team records</p>
            <h2 className="mt-2 font-heading text-5xl uppercase">
              Content-managed profiles
            </h2>
            <div className="mt-8 grid gap-px bg-white/25 sm:grid-cols-2 lg:grid-cols-3">
              {team.map((member) => (
                <article key={member.id} className="bg-foreground p-6">
                  <p className="font-heading text-3xl uppercase">
                    {member.title}
                  </p>
                  <p className="mt-2 text-sm text-accent">{member.excerpt}</p>
                  <p className="mt-4 text-sm leading-6 text-white/65">
                    {member.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className="field-container grid gap-12 py-16 lg:grid-cols-2">
        <div>
          <p className="field-label">Illustrative content modules</p>
          <h2 className="mt-2 font-heading text-4xl uppercase">Sample notes</h2>
          <div className="mt-5 space-y-4">
            {testimonials.map((item) => (
              <blockquote
                key={item.id}
                className="border-t-4 border-accent bg-white p-5"
              >
                <p className="text-sm leading-6 text-muted-foreground">
                  {item.body}
                </p>
                <footer className="field-label mt-3">{item.title}</footer>
              </blockquote>
            ))}
          </div>
          {partners.length > 0 ? (
            <div className="mt-8">
              <p className="field-label">Fictional partners</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {partners.map((partner) => (
                  <span
                    key={partner.id}
                    className="border border-border bg-white px-4 py-3 text-sm font-semibold"
                  >
                    {partner.title}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div>
          <p className="field-label">Frequently asked</p>
          <h2 className="mt-2 font-heading text-4xl uppercase">
            Before you explore
          </h2>
          <Accordion className="mt-5 border-t-2 border-foreground">
            {faq.map((item) => (
              <AccordionItem key={item.id} value={String(item.id)}>
                <AccordionTrigger className="rounded-none text-left font-heading text-xl uppercase">
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-6 text-muted-foreground">
                  {item.body}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  )
}
